import {
    calendarDateToDay,
    formatTime24,
    getMoscowCalendarParts,
    moscowCalendarToUtc,
} from './dateTime';

/** Режим фильтра даты и времени */
export type DateTimeFilterMode = 'day' | 'before' | 'after' | 'range';

/** Границы фильтра в UTC с исключительной верхней границей */
export interface DateTimeFilterBounds {
    from?: string;
    to?: string;
}

/** Сохраняет выбранную дату и время календаря */
export const serializeFilterDate = (date: Date | null): string => date
    ? `${calendarDateToDay(date)}T${formatTime24(date.getTime())}`
    : '';

/** Восстанавливает дату календаря из сохранённого значения */
export const parseFilterDate = (value: unknown): Date | null => {
    if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(value)) return null;

    const result = new Date(value);

    return Number.isFinite(result.getTime()) && serializeFilterDate(result) === value
        ? result
        : null;
};

/** Возвращает текущее московское время для календаря */
export const getFilterNow = (): Date => {
    const { date, time } = getMoscowCalendarParts(new Date().toISOString());
    const [hours, minutes] = time.split(':').map(Number);
    date.setHours(hours, minutes);

    return date;
};

/** Преобразует условия календаря в границы запроса */
export const dateTimeFilterToBounds = (value: unknown): DateTimeFilterBounds | null => {
    if (!Array.isArray(value) || (value.length !== 2 && value.length !== 3)) return null;

    const [mode, start, end] = value;
    const first = parseFilterDate(start);
    const last = parseFilterDate(end);

    if (!first) return null;

    const from = moscowCalendarToUtc(first, formatTime24(first.getTime()));

    /** Возвращает границу после выбранной минуты */
    const throughMinute = (date: Date): string => new Date(
        Date.parse(moscowCalendarToUtc(date, formatTime24(date.getTime()))) + 60_000,
    ).toISOString();

    if (mode === 'before') return { to: throughMinute(first) };
    if (mode === 'after') return { from };

    if (mode === 'day') {
        const next = new Date(first);
        next.setDate(next.getDate() + 1);

        return {
            from: moscowCalendarToUtc(first, '00:00'),
            to: moscowCalendarToUtc(next, '00:00'),
        };
    }

    if (mode !== 'range' || !last || first.getTime() > last.getTime()) return null;

    return {
        from,
        to: throughMinute(last),
    };
};

/** Подписывает применённое условие даты и времени */
export const describeDateTimeFilter = (value: unknown): string => {
    if (!dateTimeFilterToBounds(value) || !Array.isArray(value)) return '';

    const [mode, start, end] = value;
    const first = parseFilterDate(start);
    const last = parseFilterDate(end);

    /** Подписывает выбранные поля календаря */
    const format = (date: Date | null): string => date?.toLocaleString('ru-RU', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    }) ?? '';

    if (mode === 'day') return first?.toLocaleDateString('ru-RU') ?? '';
    if (mode === 'before') return `До ${format(first)} включительно`;
    if (mode === 'after') return `Начиная с ${format(first)}`;

    return `${format(first)} — ${format(last)}`;
};
